import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useVideo } from "../../context/VideoContext";
import useVideoManager from "../../hooks/useVideoManager";
import { convertCuesToSRT } from "../../utils/formatTime";
import { safeGetItem, safeSetItem } from "../../utils/safeStorage";
import Controls from "./Controls";
import SubtitleOverlay from "./SubtitleOverlay";
import SpeedDial from "./SpeedDial";
import SettingsPanel from "./SettingsPanel";
import DragDropZone from "./DragDropZone";
import LoadingModal from "./LoadingModal";
import "./VideoPlayer.css";
import SubtitleUploadModal from "./SubtitleUploadModal";
import SubtitleNotFoundModal from "./SubtitleNotFoundModal";
import DelayBox from "./DelayBox";
import { useShadowTechnique } from "../../context/ShadowTechniqueContext";
import ShadowTechniqueSettings from "./ShadowTechniqueSettings";
import ShadowProcessBox from "./ShadowProcessBox";
import SubtitleSettingsPanel from "./SubtitleSettings/SubtitleSettingsPanel";
import SubtitleSelector from "./SubtitleSettings/SubtitleSelector";
import SubtitleStyle from "./SubtitleSettings/SubtitleStyle";
import { useSubtitleSettings } from "../../context/SubtitleSettingsContext";
import { useNotification } from "../../context/NotificationContext";
import { useSelectedSubtitles } from "../../context/SelectedSubtitlesContext";
import NotificationContainer from "../common/Notification/NotificationContainer";
import SubtitleSearchModal from "../subtitle/SubtitleSearchModal";
import { usePackagesModal } from "../../context/PackagesModalContext";
import PackagesModal from "../PackagesModal/PackagesModal";
import InfiniteRepeatIndicator from "./InfiniteRepeatIndicator";
// 🎯 SubtitleComparisonModal حذف شد - کد مرده (مسیر data.results هرگز رخ نمی‌دهد)
import SubtitleComparisonWizard from "../SubtitleComparison/SubtitleComparisonWizard";
import CollectionSubtitleSearch from "../CollectionSubtitles/CollectionSubtitleSearch";
// 🎯 OtherUsersSubtitlesModal حذف شد - در SubtitleNotFoundModal ادغام شد
import SaveCollectionModal from "./SaveCollectionModal";
import MobileFullscreenPrompt from "./MobileFullscreenPrompt";
import VolumeIndicator from "./VolumeIndicator";
import TutorialGuideModal from "./TutorialGuideModal";
import MacVideoWarning from "./MacVideoWarning";

// Custom hook برای مدیریت auto-hide
const useAutoHideControls = (
  containerRef,
  videoRef,
  isPlaying,
  controlsAutoHide,
  hideControlsDelay,
  togglePlay,
  toggleFullscreen,
  toggleMute,
  handleVolumeChange,
  volume,
  handleSeek,
  isUserInteracting,
) => {
  const [showControls, setShowControls] = useState(true);
  const hideControlsTimeoutRef = useRef(null);
  const lastActivityTimeRef = useRef(Date.now());
  const mouseMoveHandlerRef = useRef(null);

  const resetActivityTimer = useCallback(() => {
    if (!controlsAutoHide) {
      setShowControls(true);
      return;
    }

    lastActivityTimeRef.current = Date.now();
    setShowControls(true);

    if (hideControlsTimeoutRef.current) {
      clearTimeout(hideControlsTimeoutRef.current);
    }

    hideControlsTimeoutRef.current = setTimeout(() => {
      const timeSinceLastActivity = Date.now() - lastActivityTimeRef.current;

      if (timeSinceLastActivity >= hideControlsDelay) {
        setShowControls(false);
      }
    }, hideControlsDelay);
  }, [controlsAutoHide, hideControlsDelay]);

  const handleMouseMove = useCallback(() => {
    resetActivityTimer();
  }, [resetActivityTimer]);

  useEffect(() => {
    mouseMoveHandlerRef.current = handleMouseMove;
  }, [handleMouseMove]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleContainerMouseMove = () => {
      if (mouseMoveHandlerRef.current) {
        mouseMoveHandlerRef.current();
      }
    };

    container.addEventListener("mousemove", handleContainerMouseMove);

    if (controlsAutoHide) {
      resetActivityTimer();
    }

    return () => {
      container.removeEventListener("mousemove", handleContainerMouseMove);

      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
      }
    };
  }, [containerRef, controlsAutoHide, resetActivityTimer]);

  // 👈👈👈 افکت جدید برای isUserInteracting
  useEffect(() => {
    if (isUserInteracting) {
      setShowControls(true);
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
        hideControlsTimeoutRef.current = null;
      }
    } else {
      if (controlsAutoHide && isPlaying) {
        resetActivityTimer();
      }
    }
  }, [isUserInteracting, controlsAutoHide, isPlaying, resetActivityTimer]);

  useEffect(() => {
    if (!controlsAutoHide || !isPlaying) {
      setShowControls(true);
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
        hideControlsTimeoutRef.current = null;
      }
    } else {
      resetActivityTimer();
    }
  }, [controlsAutoHide, isPlaying, resetActivityTimer]);

  return [showControls, setShowControls, resetActivityTimer];
};

// ============== هوک مدیریت تاچ موبایل ==============
const useMobileTouch = (containerRef, videoRef, options) => {
  const {
    togglePlay,
    handleSeek,
    handleVolumeChange,
    duration,
    volume,
    isMobileDevice,
    onVolumeChangeStart, // 👈 جدید
    onVolumeChangeEnd, // 👈 جدید
    isAnyModalOpen,
  } = options;

  const touchStateRef = useRef({
    startX: 0,
    startY: 0,
    startTime: 0,
    lastTapTime: 0,
    startVolume: 0,
    isVolumeGesture: false,
    movedSignificantly: false,
    tapTimer: null,
    wasPlayingBeforeDoubleTap: false, // 👈 ذخیره وضعیت پخش قبل از دوبار تاچ
    targetTouched: null,
  });

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !isMobileDevice()) return;

    // 👈 ثابت‌های قابل تنظیم
    const SEEK_ZONE_WIDTH = 0.2; // ۲۰٪
    const DOUBLE_TAP_DELAY = 300; // ms
    const CENTER_START = SEEK_ZONE_WIDTH;
    const CENTER_END = 1 - SEEK_ZONE_WIDTH;

    const resetState = () => {
      const state = touchStateRef.current;
      state.isVolumeGesture = false;
      state.movedSignificantly = false;
      state.startX = 0;
      state.startY = 0;
      state.wasPlayingBeforeDoubleTap = false;
      state.targetTouched = null;
    };

    const handleTouchStart = (e) => {
      if (isAnyModalOpen) return;
      if (e.touches.length !== 1) {
        if (touchStateRef.current.tapTimer) {
          clearTimeout(touchStateRef.current.tapTimer);
        }
        touchStateRef.current.lastTapTime = 0;
        return;
      }

      const touch = e.touches[0];
      const rect = container.getBoundingClientRect();
      const x = touch.clientX - rect.left;
      const y = touch.clientY - rect.top;
      const width = rect.width;

      const now = Date.now();
      const state = touchStateRef.current;

      state.startX = x;
      state.startY = y;
      state.startTime = now;
      state.startVolume = volume;
      state.movedSignificantly = false;

      // 👇 ذخیره عنصری که تاچ روش شروع شده
      state.targetTouched = e.target;

      // تشخیص ژست کنترل صدا
      if (x < width * 0.5) {
        state.isVolumeGesture = true;
      } else {
        state.isVolumeGesture = false;
      }

      // منطق دوبار تاچ
      const timeSinceLastTap = now - state.lastTapTime;

      if (timeSinceLastTap < DOUBLE_TAP_DELAY && timeSinceLastTap > 0) {
        // 👈 دوبار تاچ تشخیص داده شد

        // پاک کردن تایمر تاپ اول
        if (state.tapTimer) {
          clearTimeout(state.tapTimer);
          state.tapTimer = null;
        }

        // 👈 اگر لمس اول باعث پخش/توقف شده بود، برگردون به حالت قبل
        if (state.wasPlayingBeforeDoubleTap !== null) {
          const currentlyPlaying = !videoRef.current?.paused;
          if (currentlyPlaying !== state.wasPlayingBeforeDoubleTap) {
            togglePlay();
          }
        }

        // حالا seek کن
        if (x < width * CENTER_START) {
          // ۲۰٪ چپ: ۱۰ ثانیه عقب
          e.preventDefault();
          handleSeek(Math.max(0, (videoRef.current?.currentTime || 0) - 10));
        } else if (x > width * CENTER_END) {
          // ۲۰٪ راست: ۱۰ ثانیه جلو
          e.preventDefault();
          handleSeek(
            Math.min(
              duration || videoRef.current?.duration || 0,
              (videoRef.current?.currentTime || 0) + 10,
            ),
          );
        }

        // ریست کردن
        state.lastTapTime = 0;
        state.wasPlayingBeforeDoubleTap = false;
        return;
      }

      // 👈 اولین تاپ: ذخیره وضعیت پخش فعلی
      state.lastTapTime = now;
      state.wasPlayingBeforeDoubleTap = !videoRef.current?.paused;
    };

    const handleTouchMove = (e) => {
      if (isAnyModalOpen) return;
      if (e.touches.length !== 1) return;

      const touch = e.touches[0];
      const state = touchStateRef.current;
      const deltaY = state.startY - touch.clientY;
      const deltaX = touch.clientX - state.startX;

      if (Math.abs(deltaY) > 10 || Math.abs(deltaX) > 10) {
        state.movedSignificantly = true;

        if (state.tapTimer) {
          clearTimeout(state.tapTimer);
          state.tapTimer = null;
        }

        if (state.isVolumeGesture && Math.abs(deltaY) > Math.abs(deltaX)) {
          e.preventDefault();
          if (onVolumeChangeStart) onVolumeChangeStart();

          const containerHeight = container.getBoundingClientRect().height;
          const volumeChange = (deltaY / containerHeight) * 1.5;
          const newVolume = Math.max(
            0,
            Math.min(1, state.startVolume + volumeChange),
          );

          handleVolumeChange(newVolume);
        }
      }
    };

    const handleTouchEnd = (e) => {
      if (isAnyModalOpen) return;
      const state = touchStateRef.current;
      if (onVolumeChangeEnd) onVolumeChangeEnd();

      if (state.movedSignificantly) {
        resetState();
        return;
      }

      if (state.lastTapTime === 0) {
        resetState();
        return;
      }
      if (state.targetTouched) {
        const isOnControls = state.targetTouched.closest(".controls");
        const isOnOverlay = state.targetTouched.closest(".subtitle-overlay");
        const isOnVideoTitle = state.targetTouched.closest(
          ".video-title-overlay",
        );

        if (isOnControls || isOnOverlay || isOnVideoTitle) {
          resetState();
          return; // 👈 اجازه نده togglePlay اجرا بشه
        }
      }

      if (state.lastTapTime === 0) {
        resetState();
        return;
      }
      const rect = container.getBoundingClientRect();
      const endX = state.startX;

      if (endX < rect.width * CENTER_START || endX > rect.width * CENTER_END) {
        state.tapTimer = setTimeout(() => {
          if (!isAnyModalOpen) {
            togglePlay();
          }
          resetState();
        }, DOUBLE_TAP_DELAY);
        return;
      }

      state.tapTimer = setTimeout(() => {
        // 👈 اینو اضافه کن
        if (!isAnyModalOpen) {
          togglePlay();
        }
        resetState();
      }, DOUBLE_TAP_DELAY);

      state.lastTapTime = Date.now();
    };

    const handleTouchCancel = () => {
      if (touchStateRef.current.tapTimer) {
        clearTimeout(touchStateRef.current.tapTimer);
      }
      resetState();
    };

    container.addEventListener("touchstart", handleTouchStart, {
      passive: false,
    });
    container.addEventListener("touchmove", handleTouchMove, {
      passive: false,
    });
    container.addEventListener("touchend", handleTouchEnd);
    container.addEventListener("touchcancel", handleTouchCancel);

    return () => {
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("touchmove", handleTouchMove);
      container.removeEventListener("touchend", handleTouchEnd);
      container.removeEventListener("touchcancel", handleTouchCancel);

      if (touchStateRef.current.tapTimer) {
        clearTimeout(touchStateRef.current.tapTimer);
      }
    };
  }, [
    containerRef,
    videoRef,
    togglePlay,
    handleSeek,
    handleVolumeChange,
    duration,
    volume,
    isMobileDevice,
    isAnyModalOpen,
    onVolumeChangeEnd,
  ]);
};

const VideoPlayer = ({ onOpenHelpModal, onIOSWarning }) => {
  // ============== همه useContext hooks در ابتدا ==============
  const {
    videoRef,
    containerRef,
    videoSrc,
    togglePlay,
    showSpeedDial,
    showSettings,
    isFullscreen,
    setCurrentTime,
    setDuration,
    isPlaying,
    setIsPlaying,
    toggleMute,
    handleSeek,
    volume,
    handleVolumeChange,
    toggleFullscreen,
    hideControlsDelay,
    controlsAutoHide,
    isLoadingSubtitles,
    loadingMessage,
    showSubtitleNotFoundModal,
    setShowSubtitleNotFoundModal,
    showOtherUsersSubtitles,
    setShowOtherUsersSubtitles,
    otherUsersSubtitlesData,
    setOtherUsersSubtitlesData,
    currentMovieName,
    videoMetadata,
    changePlaybackRate,
    setShowSettings,
    shortcutsEnabled,
    setShortcutsEnabled,
    subtitle1Loaded,
    subtitle2Loaded,
    subtitle1Delay,
    subtitle2Delay,
    setSubtitle1Delay,
    setSubtitle2Delay,
    showDelayBox,
    setShowDelayBox,
    duration,
    playbackRate,
    subtitle1Json,
    subtitle2Json,
    isModalOpen,
    autoLoadedSub1Id,
    autoLoadedSub2Id,
    showTutorialGuide,
    setShowTutorialGuide,
  } = useVideo();

  const {
    showSettingsPanel,
    setShowSettingsPanel,
    showSelectorPanel,
    setShowSelectorPanel,
    showStylePanel,
    setShowStylePanel,
    toggleSubtitle1Display,
    toggleSubtitle2Display,
    increaseFontSize1,
    decreaseFontSize1,
    increaseFontSize2,
    decreaseFontSize2,
    settings,
  } = useSubtitleSettings();

  const {
    isOpen: isPackagesOpen,
    hidePackages,
    handleSelectPackage,
  } = usePackagesModal();

  const { checkForNextRepeat, showProcessBox: showShadowProcessBox } =
    useShadowTechnique();
  const { showInfo } = useNotification();
  const { activeSubtitles } = useSelectedSubtitles();

  // ============== useState hooks ==============
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [uploadedSubtitleFiles, setUploadedSubtitleFiles] = useState(null);
  const [showShadowSettings, setShowShadowSettings] = useState(false);
  const [testBox, setTestBox] = useState(false);
  const [isInfiniteRepeatActive, setIsInfiniteRepeatActive] = useState(false);
  const [lastRepeatedSubtitle, setLastRepeatedSubtitle] = useState(null);
  const [showInfiniteIndicator, setShowInfiniteIndicator] = useState(false); // اضافه کنید
  const [showWizardModal, setShowWizardModal] = useState(false);
  // 🎯 showComparisonModal و comparisonSubtitles حذف شدند - کد مرده (مسیر data.results هرگز رخ نمی‌دهد)
  const [showCollectionSearch, setShowCollectionSearch] = useState(false);
  const [showSaveCollection, setShowSaveCollection] = useState(false);
  const [showMobileFullscreenPrompt, setShowMobileFullscreenPrompt] =
    useState(false);
  const [showDesktopFullscreenPrompt, setShowDesktopFullscreenPrompt] =
    useState(false);
  const [showDesktopVolume, setShowDesktopVolume] = useState(false);
  const [showMobileVolume, setShowMobileVolume] = useState(false);
  const [isUserInteracting, setIsUserInteracting] = useState(false);
  const [isAnyModalOpen, setIsAnyModalOpen] = useState(false);
  const [showMacWarning, setShowMacWarning] = useState(false);

  // ============== useRef hooks ==============
  const hasLoadedRef = useRef(false);
  const infiniteRepeatIntervalRef = useRef(null);
  const volumeTimeoutRef = useRef(null);
  const infiniteRepeatDataRef = useRef(null); // برای ذخیره اطلاعات زیرنویس در حال تکرار
  const videoFileExtensionRef = useRef(null);

  // ============== تعریف توابع کمکی (قبل از استفاده) ==============
  const { handleFileProcessing, loadLocalSubtitle } = useVideoManager();

  // 🎯 تابع تشخیص موبایل
  const isMobileDevice = useCallback(() => {
    return (
      /Android|iPhone|iPad|iPod|webOS/i.test(navigator.userAgent) ||
      "ontouchstart" in window ||
      navigator.maxTouchPoints > 0
    );
  }, []);

  // 🎯 هوک مدیریت تمام صفحه - فقط در انتخاب ویدیوی جدید
  useEffect(() => {
    if (!videoSrc) return;

    const autoFullscreen = safeGetItem("autoFullscreenOnVideoSelect");

    if (autoFullscreen === null) {
      safeSetItem("autoFullscreenOnVideoSelect", "true");
    }

    const shouldAutoFullscreen =
      autoFullscreen === null || autoFullscreen === "true";

    if (shouldAutoFullscreen) {
      // اگر isLoadingSubtitles تموم شده بود (لودینگ بسته شده بود)
      const timer = setTimeout(async () => {
        if (!isLoadingSubtitles) {
          if (isMobileDevice()) {
            // 🎯 روی موبایل: نمایش باکس پیشنهاد
            setShowMobileFullscreenPrompt(true);
          } else {
            // 🎯 روی دسکتاپ: مستقیم برو تمام صفحه
            try {
              await toggleFullscreen();
            } catch (error) {
              // چک کن آیا خطای پرمیشن هست
              if (
                error instanceof TypeError &&
                error.message.includes("Permissions check failed")
              ) {
                // نمایش باکس دسکتاپ مشابه موبایل
                setShowDesktopFullscreenPrompt(true);
              } else {
                console.log("Auto fullscreen not supported:", error);
              }
            }
          }
        }
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [videoSrc, isLoadingSubtitles]); // فقط زمانی اجرا میشه که ویدیوی جدید انتخاب شد

  // 🎯 هندلرهای باکس موبایل
  const handleAcceptFullscreen = useCallback(() => {
    setShowMobileFullscreenPrompt(false);
    try {
      toggleFullscreen();
    } catch (error) {
      console.log("Mobile fullscreen failed:", error);
    }
  }, [toggleFullscreen]);

  const handleDeclineFullscreen = useCallback(() => {
    setShowMobileFullscreenPrompt(false);
    // هیچ کاری نکن، فقط باکس رو ببند
  }, []);

  // 🎯 هندلرهای باکس دسکتاپ (وقتی پرمیشن فول اسکرین داده نمیشه)
  const handleAcceptDesktopFullscreen = useCallback(() => {
    setShowDesktopFullscreenPrompt(false);
    try {
      toggleFullscreen();
    } catch (error) {
      console.log("Desktop fullscreen retry failed:", error);
    }
  }, [toggleFullscreen]);

  const handleDeclineDesktopFullscreen = useCallback(() => {
    setShowDesktopFullscreenPrompt(false);
    // کاربر نمی‌خواد فول اسکرین شه، فقط باکس رو ببند
  }, []);

  const buildSubMetadata = useCallback((sub, language) => ({
    sourceType: sub.source || sub.sourceType || "local",
    language: language,
    nameOfMovie: sub.title || sub.nameOfMovie || sub.movieName || "",
    movieName: sub.movieName || sub.nameOfMovie || sub.title || "",
    onlineId: sub.onlineId || sub.subtitlesUploadId || null,
    subtitlesUploadId: sub.subtitlesUploadId || sub.onlineId || null,
    videoType: sub.videoType || sub.type || "movie",
    season: sub.season?.toString() || "",
    episode: sub.episode?.toString() || "",
    fileName: sub.fileName || "",
    coverUrl: sub.coverUrl || "",
    otherTags: sub.otherTags || [],
    productionYear: sub.productionYear || "",
    _source: sub._source || "comparison",
  }), []);

  const handleWizardComplete = useCallback((data) => {
    if (!data) return;
    // تماشای فیلم با زیرنویس مرجع
    if (data.watchOnly && data.subtitle && data.subtitle.cues) {
      const srtContent = convertCuesToSRT(data.subtitle.cues);
      const file = new File([srtContent], data.subtitle.fileName || `subtitle_${data.language}.srt`, { type: "text/plain" });
      loadLocalSubtitle(file, data.language, data.targetNumber || 1, buildSubMetadata(data.subtitle, data.language));
      setShowWizardModal(false);
      return;
    }
    // تماشای فیلم با دو زیرنویس همزمان
    if (data.watchTwo && data.subtitle1 && data.subtitle2) {
      const srt1 = convertCuesToSRT(data.subtitle1.cues);
      const file1 = new File([srt1], data.subtitle1.fileName || `sub1_${data.language1}.srt`, { type: "text/plain" });
      loadLocalSubtitle(file1, data.language1, 1, buildSubMetadata(data.subtitle1, data.language1));
      const srt2 = convertCuesToSRT(data.subtitle2.cues);
      const file2 = new File([srt2], data.subtitle2.fileName || `sub2_${data.language2}.srt`, { type: "text/plain" });
      setTimeout(() => {
        loadLocalSubtitle(file2, data.language2, 2, buildSubMetadata(data.subtitle2, data.language2));
      }, 200);
      setShowWizardModal(false);
      return;
    }
    // 🎯 مسیر data.results حذف شد - SubtitleComparisonWizard هرگز data.results برنمی‌گرداند
  }, [loadLocalSubtitle, buildSubMetadata]);

  const isMacOS = useCallback(() => {
    return /Mac|mac/i.test(navigator.platform) && !isIOS();
  }, []);

  // 🎯 تشخیص iOS
  const isIOS = () => {
    return (
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
    );
  };

  // 🎯 فرمت‌هایی که در iOS پشتیبانی نمی‌شوند
  const IOS_UNSUPPORTED_EXTENSIONS = [
    ".mkv",
    ".webm",
    ".flv",
    ".wmv",
    ".avi",
    ".divx",
    ".xvid",
    ".rm",
    ".rmvb",
    ".asf",
    ".vob",
    ".mpg",
    ".mpeg",
    ".m2v",
    ".m1v",
    ".f4v",
    ".ts",
    ".mts",
    ".m2ts",
    ".ogv",
  ];

  const handleNewVideoSelect = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";

    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      const extension = file.name.split(".").pop().toLowerCase();
      videoFileExtensionRef.current = extension;

      // 🎯 فقط در iOS: نمایش مودال خوشگل
      if (isIOS()) {
        const fileName = file.name.toLowerCase();
        const isUnsupported = IOS_UNSUPPORTED_EXTENSIONS.some((ext) =>
          fileName.endsWith(ext),
        );

        if (isUnsupported) {
          const fileExtension = extension.toUpperCase();
          onIOSWarning({ isOpen: true, fileExtension });
          return;
        }
      }

      // اگر فایل معتبر بود، پردازش کن
      if (file) {
        await handleFileProcessing(file);
      }
    };

    input.click();
  }, [handleFileProcessing]);
  // تابع تبدیل زمان رشته‌ای به ثانیه
  const convertTimeToSeconds = useCallback((timeString) => {
    if (!timeString) return 0;
    const parts = timeString.split(/[:,.]/);
    if (parts.length >= 3) {
      const hours = parseInt(parts[0], 10);
      const minutes = parseInt(parts[1], 10);
      const seconds = parseInt(parts[2], 10);
      const milliseconds = parts.length > 3 ? parseInt(parts[3], 10) : 0;
      return hours * 3600 + minutes * 60 + seconds + milliseconds / 1000;
    }
    return 0;
  }, []);
  // هندلر جدید
  const handleCollectionSearchClick = () => {
    setShowCollectionSearch(true);
  };
  const handleSaveCollectionClick = () => {
    setShowSaveCollection(true);
  };
  // ============== توابع تکرار زیرنویس ==============
  const findCurrentSubtitle = useCallback(() => {
    let subtitles = null;
    if (subtitle1Json && subtitle1Json.length > 0) {
      subtitles = subtitle1Json;
    } else if (subtitle2Json && subtitle2Json.length > 0) {
      subtitles = subtitle2Json;
    }

    if (!subtitles || subtitles.length === 0) return null;

    const currentTime = videoRef.current?.currentTime || 0;

    for (let i = 0; i < subtitles.length; i++) {
      const sub = subtitles[i];
      const startTime = convertTimeToSeconds(sub.startTime);
      const endTime = convertTimeToSeconds(sub.endTime);

      if (currentTime >= startTime && currentTime <= endTime) {
        return { subtitle: sub, index: i, subtitles };
      }
    }

    for (let i = subtitles.length - 1; i >= 0; i--) {
      const sub = subtitles[i];
      const endTime = convertTimeToSeconds(sub.endTime);
      const timeSinceEnd = currentTime - endTime;

      if (timeSinceEnd > 0 && timeSinceEnd <= 1) {
        return { subtitle: sub, index: i, subtitles };
      }
    }

    return null;
  }, [subtitle1Json, subtitle2Json, videoRef, convertTimeToSeconds]);

  // تابع توقف تکرار بینهایت
  const stopInfiniteRepeat = useCallback(() => {
    if (infiniteRepeatIntervalRef.current) {
      clearInterval(infiniteRepeatIntervalRef.current);
      infiniteRepeatIntervalRef.current = null;
    }
    infiniteRepeatDataRef.current = null;
    setIsInfiniteRepeatActive(false);
    setShowInfiniteIndicator(false); // مخفی کردن indicator
    // showInfo("تکرار بی‌نهایت متوقف شد", { duration: 1500 });
  }, [showInfo]);

  // تابع شروع تکرار بینهایت
  const startInfiniteRepeat = useCallback(() => {
    const result = findCurrentSubtitle();

    if (!result) {
      showInfo("زیرنویسی برای تکرار بی‌نهایت یافت نشد", {
        duration: 1500,
        type: "warning",
      });
      return false;
    }

    const { subtitle, index } = result;
    const startTime = convertTimeToSeconds(subtitle.startTime);
    const endTime = convertTimeToSeconds(subtitle.endTime);

    // ذخیره اطلاعات در ref برای استفاده در interval
    infiniteRepeatDataRef.current = {
      startTime,
      endTime,
    };

    setLastRepeatedSubtitle({ subtitle, index });
    setIsInfiniteRepeatActive(true);

    // رفتن به ابتدای زیرنویس
    handleSeek(startTime);

    // اطمینان از پخش ویدیو
    if (videoRef.current && videoRef.current.paused) {
      videoRef.current.play().catch(console.error);
    }

    // پاک کردن interval قبلی
    if (infiniteRepeatIntervalRef.current) {
      clearInterval(infiniteRepeatIntervalRef.current);
    }

    // تنظیم interval جدید برای تکرار - استفاده از ref به جای state
    infiniteRepeatIntervalRef.current = setInterval(() => {
      if (videoRef.current && infiniteRepeatDataRef.current) {
        const currentTime = videoRef.current.currentTime;
        const { startTime, endTime } = infiniteRepeatDataRef.current;

        // اگر ویدیو به انتهای زیرنویس رسید یا از آن گذشت، دوباره به اول برگرد
        if (currentTime >= endTime - 0.05) {
          videoRef.current.currentTime = startTime;
          if (videoRef.current.paused) {
            videoRef.current.play().catch(console.error);
          }
        }
      }
    }, 100);
    setShowInfiniteIndicator(true);
    // showInfo(
    //   "🔁 تکرار بی‌نهایت فعال شد - برای توقف دوباره کلید Shift+S را بزنید",
    //   { duration: 3000, type: "info" }
    // );

    return true;
  }, [
    findCurrentSubtitle,
    handleSeek,
    showInfo,
    videoRef,
    convertTimeToSeconds,
  ]);

  // تابع تکرار معمولی زیرنویس
  const repeatCurrentSubtitle = useCallback(() => {
    // اگر تکرار بینهایت فعال است، اول آن را متوقف کن
    if (isInfiniteRepeatActive) {
      stopInfiniteRepeat();
      setShowInfiniteIndicator(false); // مخفی کردن indicator
    }

    const result = findCurrentSubtitle();

    if (!result) {
      showInfo("زیرنویسی برای تکرار یافت نشد", {
        duration: 1500,
        type: "warning",
      });
      return;
    }

    const { subtitle } = result;
    const startTime = convertTimeToSeconds(subtitle.startTime);

    handleSeek(Math.max(0, startTime - 0.05));

    const subtitleText =
      subtitle.text || subtitle.line || subtitle.content || "";
    // showInfo(`تکرار: "${subtitleText.substring(0, 50)}..."`, {
    //   duration: 1500,
    // });
  }, [
    findCurrentSubtitle,
    handleSeek,
    showInfo,
    isInfiniteRepeatActive,
    stopInfiniteRepeat,
    convertTimeToSeconds,
  ]);

  // تابع toggle برای تکرار بینهایت
  const toggleInfiniteRepeat = useCallback(() => {
    if (isInfiniteRepeatActive) {
      stopInfiniteRepeat();
    } else {
      startInfiniteRepeat();
    }
  }, [isInfiniteRepeatActive, startInfiniteRepeat, stopInfiniteRepeat]);

  // ============== توابع رفتن به زیرنویس قبلی/بعدی ==============
  const goToNextSubtitle = useCallback(() => {
    if (!videoRef.current || !duration) return;

    let subtitles = null;
    if (subtitle1Json && subtitle1Json.length > 0) {
      subtitles = subtitle1Json;
    } else if (subtitle2Json && subtitle2Json.length > 0) {
      subtitles = subtitle2Json;
    }

    if (!subtitles || subtitles.length === 0) return;

    const adjustedTime = (videoRef.current.currentTime || 0) + 0.11;

    let currentSubtitleIndex = -1;
    for (let i = 0; i < subtitles.length; i++) {
      const sub = subtitles[i];
      const startTime = convertTimeToSeconds(sub.startTime);
      const endTime = convertTimeToSeconds(sub.endTime);
      if (adjustedTime >= startTime && adjustedTime <= endTime) {
        currentSubtitleIndex = i;
        break;
      }
    }

    let targetTime;

    if (
      currentSubtitleIndex !== -1 &&
      currentSubtitleIndex < subtitles.length - 1
    ) {
      const nextSubtitle = subtitles[currentSubtitleIndex + 1];
      targetTime = convertTimeToSeconds(nextSubtitle.startTime) - 0.1;
    } else if (currentSubtitleIndex === subtitles.length - 1) {
      targetTime = duration;
    } else {
      let nextSubtitle = null;
      for (let i = 0; i < subtitles.length; i++) {
        const startTime = convertTimeToSeconds(subtitles[i].startTime);
        if (startTime > adjustedTime) {
          nextSubtitle = subtitles[i];
          break;
        }
      }
      if (nextSubtitle) {
        targetTime = convertTimeToSeconds(nextSubtitle.startTime) - 0.1;
      } else {
        targetTime = duration;
      }
    }

    targetTime = Math.min(duration, Math.max(0, targetTime));
    handleSeek(targetTime);

    if (isInfiniteRepeatActive) {
      setShowInfiniteIndicator(false);
      stopInfiniteRepeat();
    }
  }, [
    videoRef,
    duration,
    subtitle1Json,
    subtitle2Json,
    convertTimeToSeconds,
    handleSeek,
    isInfiniteRepeatActive,
    stopInfiniteRepeat,
  ]);

  const goToPreviousSubtitle = useCallback(() => {
    if (!videoRef.current || !duration) return;

    let subtitles = null;
    if (subtitle1Json && subtitle1Json.length > 0) {
      subtitles = subtitle1Json;
    } else if (subtitle2Json && subtitle2Json.length > 0) {
      subtitles = subtitle2Json;
    }

    if (!subtitles || subtitles.length === 0) return;

    const adjustedTime = (videoRef.current.currentTime || 0) - 0.1;

    let currentSubtitleIndex = -1;
    for (let i = 0; i < subtitles.length; i++) {
      const sub = subtitles[i];
      const startTime = convertTimeToSeconds(sub.startTime);
      const endTime = convertTimeToSeconds(sub.endTime);
      if (adjustedTime >= startTime && adjustedTime <= endTime) {
        currentSubtitleIndex = i;
        break;
      }
    }

    let targetTime;

    if (currentSubtitleIndex > 0) {
      const previousSubtitle = subtitles[currentSubtitleIndex - 1];
      targetTime = convertTimeToSeconds(previousSubtitle.startTime) - 0.1;
    } else if (currentSubtitleIndex === 0) {
      targetTime = 0;
    } else {
      let previousSubtitle = null;
      for (let i = subtitles.length - 1; i >= 0; i--) {
        const startTime = convertTimeToSeconds(subtitles[i].startTime);
        if (startTime < adjustedTime) {
          previousSubtitle = subtitles[i];
          break;
        }
      }
      if (previousSubtitle) {
        targetTime = convertTimeToSeconds(previousSubtitle.startTime) - 0.1;
      } else {
        targetTime = 0;
      }
    }

    targetTime = Math.max(0, targetTime);
    handleSeek(targetTime);

    if (isInfiniteRepeatActive) {
      setShowInfiniteIndicator(false);
      stopInfiniteRepeat();
    }
  }, [
    videoRef,
    duration,
    subtitle1Json,
    subtitle2Json,
    convertTimeToSeconds,
    handleSeek,
    isInfiniteRepeatActive,
    stopInfiniteRepeat,
  ]);

  // تابع adjustSubtitleDelay
  const adjustSubtitleDelay = useCallback(
    (subNumber, deltaMs) => {
      const video = videoRef.current;
      if (!video) return;

      const textTracks = video.textTracks;
      let targetTrack = null;

      for (let i = 0; i < textTracks.length; i++) {
        const track = textTracks[i];
        if (subNumber === 1 && track.language === "en") {
          targetTrack = track;
          break;
        } else if (subNumber === 2 && track.language === "fa") {
          targetTrack = track;
          break;
        }
      }

      if (!targetTrack) {
        for (let i = 0; i < textTracks.length; i++) {
          const track = textTracks[i];
          if (track.id === `subtitle-track-${subNumber}`) {
            targetTrack = track;
            break;
          }
        }
      }

      if (!targetTrack || !targetTrack.cues) return;

      const currentDelay = subNumber === 1 ? subtitle1Delay : subtitle2Delay;
      const newDelay = Math.round((currentDelay + deltaMs) * 10) / 10;

      Array.from(targetTrack.cues).forEach((cue) => {
        cue.startTime += deltaMs / 1000;
        cue.endTime += deltaMs / 1000;
      });

      if (subNumber === 1) {
        setSubtitle1Delay(newDelay);
      } else {
        setSubtitle2Delay(newDelay);
      }
    },
    [
      subtitle1Delay,
      subtitle2Delay,
      setSubtitle1Delay,
      setSubtitle2Delay,
      videoRef,
    ],
  );

  // ============== مپینگ کلیدها ==============
  const keyActionMap = {
    Space: "togglePlay",
    Enter: "toggleFullscreen",
    NumpadEnter: "toggleFullscreen",
    Escape: "closeModals",
    KeyM: "toggleMute",
    KeyA: "previousSubtitle",
    KeyS: "repeatCurrentSubtitle",
    KeyD: "nextSubtitle",
    ArrowLeft: "seekBackward",
    ArrowRight: "seekForward",
    ArrowUp: "volumeUp",
    ArrowDown: "volumeDown",
    KeyT: "toggleSubtitle1",
    KeyY: "toggleSubtitle2",
    KeyQ: "increaseFontSize1",
    KeyW: "decreaseFontSize1",
    KeyE: "increaseFontSize2",
    KeyR: "decreaseFontSize2",
    KeyC: "openSubtitleSettings",
    KeyH: "openHelpModal",
    BracketLeft: "delayBase",
    BracketRight: "delayBase",
  };

  // ============== تابع executeAction ==============
  const executeAction = useCallback(
    (action, shiftKey = false, code = null) => {
      if (!videoRef.current) return false;

      switch (action) {
        case "toggleSubtitle1":
          toggleSubtitle1Display?.();
          showInfo(
            `زیرنویس یک ${
              settings?.subtitle1?.displayMode != "hidden" ? "پنهان" : "نمایش"
            } شد`,
            { duration: 1500 },
          );
          break;
        case "toggleSubtitle2":
          toggleSubtitle2Display?.();
          showInfo(
            `زیرنویس دو ${
              settings?.subtitle2?.displayMode != "hidden" ? "پنهان" : "نمایش"
            } شد`,
            { duration: 1500 },
          );
          break;
        case "increaseFontSize1":
          increaseFontSize1?.();
          showInfo(`اندازه زیرنویس یک: ${settings?.subtitle1?.fontSize}px`, {
            duration: 1000,
          });
          break;
        case "decreaseFontSize1":
          decreaseFontSize1?.();
          showInfo(`اندازه زیرنویس یک: ${settings?.subtitle1?.fontSize}px`, {
            duration: 1000,
          });
          break;
        case "increaseFontSize2":
          increaseFontSize2?.();
          showInfo(`اندازه زیرنویس دو: ${settings?.subtitle2?.fontSize}px`, {
            duration: 1000,
          });
          break;
        case "decreaseFontSize2":
          decreaseFontSize2?.();
          showInfo(`اندازه زیرنویس دو: ${settings?.subtitle2?.fontSize}px`, {
            duration: 1000,
          });
          break;
        case "togglePlay":
          togglePlay();
          break;
        case "toggleFullscreen":
          toggleFullscreen();
          break;
        case "toggleMute":
          toggleMute();
          break;
        case "seekBackward":
          handleSeek(Math.max(0, videoRef.current.currentTime - 5));
          break;
        case "seekForward":
          handleSeek(
            Math.min(
              videoRef.current.duration,
              videoRef.current.currentTime + 5,
            ),
          );
          break;
        case "volumeUp":
          handleVolumeChange(Math.min(1, volume + 0.1));
          break;
        case "volumeDown":
          handleVolumeChange(Math.max(0, volume - 0.1));
          break;
        case "closeModals":
          setShowSettings(false);
          setShowSubtitleNotFoundModal(false);
          setShowSettingsPanel(false);
          setShowSelectorPanel(false);
          setShowStylePanel(false);
          setShowShadowSettings(false);
          break;
        case "previousSubtitle":
          goToPreviousSubtitle();
          break;
        case "nextSubtitle":
          goToNextSubtitle();
          break;
        case "repeatCurrentSubtitle":
          if (shiftKey) {
            toggleInfiniteRepeat();
          } else {
            repeatCurrentSubtitle();
          }
          break;
        case "openSubtitleSettings":
          setShowSelectorPanel(true);
          break;
        case "openHelpModal":
          if (onOpenHelpModal) {
            onOpenHelpModal();
          }
          break;
        case "speedIncrease":
          if (shiftKey) {
            const newRate = Math.min(3, playbackRate + 0.1);
            changePlaybackRate(newRate);
            showInfo(`سرعت پخش: ${newRate.toFixed(1)}x`, { duration: 1000 });
          }
          break;
        case "speedDecrease":
          if (shiftKey) {
            const newRate = Math.max(0.25, playbackRate - 0.1);
            changePlaybackRate(newRate);
            showInfo(`سرعت پخش: ${newRate.toFixed(1)}x`, { duration: 1000 });
          }
          break;
        case "speedReset":
          if (shiftKey) {
            changePlaybackRate(1);
            showInfo(`سرعت پخش: 1x`, { duration: 1000 });
          }
          break;
        case "delayBase":
          if (shiftKey) {
            // Shift + [ یا Shift + ] برای زیرنویس دوم
            if (code === "BracketLeft" && subtitle2Loaded) {
              adjustSubtitleDelay(2, -100);
              setShowDelayBox(true);
              showInfo(`تاخیر زیرنویس دو: ${subtitle2Delay - 100}ms`, {
                duration: 1000,
              });
            } else if (code === "BracketRight" && subtitle2Loaded) {
              adjustSubtitleDelay(2, 100);
              setShowDelayBox(true);
              showInfo(`تاخیر زیرنویس دو: ${subtitle2Delay + 100}ms`, {
                duration: 1000,
              });
            }
          } else {
            // [ یا ] برای زیرنویس اول
            if (code === "BracketLeft" && subtitle1Loaded) {
              adjustSubtitleDelay(1, -100);
              setShowDelayBox(true);
              showInfo(`تاخیر زیرنویس یک: ${subtitle1Delay - 100}ms`, {
                duration: 1000,
              });
            } else if (code === "BracketRight" && subtitle1Loaded) {
              adjustSubtitleDelay(1, 100);
              setShowDelayBox(true);
              showInfo(`تاخیر زیرنویس یک: ${subtitle1Delay + 100}ms`, {
                duration: 1000,
              });
            }
          }
          break;
        default:
          return false;
      }
      return true;
    },
    [
      togglePlay,
      toggleFullscreen,
      toggleMute,
      handleSeek,
      volume,
      handleVolumeChange,
      changePlaybackRate,
      setShowSettings,
      setShowSubtitleNotFoundModal,
      setShowSettingsPanel,
      setShowSelectorPanel,
      setShowStylePanel,
      setShowShadowSettings,
      subtitle1Loaded,
      subtitle2Loaded,
      subtitle1Delay,
      subtitle2Delay,
      adjustSubtitleDelay,
      setShowDelayBox,
      toggleSubtitle1Display,
      toggleSubtitle2Display,
      increaseFontSize1,
      decreaseFontSize1,
      increaseFontSize2,
      decreaseFontSize2,
      settings,
      showInfo,
      playbackRate,
      onOpenHelpModal,
      goToPreviousSubtitle,
      goToNextSubtitle,
      repeatCurrentSubtitle,
      toggleInfiniteRepeat,
    ],
  );

  // ============== هندلر کیبورد ==============
  const handleKeyDown = useCallback(
    (e) => {
      if (!shortcutsEnabled) return;

      const target = e.target;
      const isTyping =
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable;

      if (isTyping) return;

      const code = e.code;
      const key = e.key;
      // window.lastKeyCode = code;

      if (e.shiftKey) {
        if (key === "+" || key === "=") {
          e.preventDefault();
          e.stopPropagation();
          executeAction("speedIncrease", true, key);
          return;
        }
        if (key === "-" || key === "_") {
          e.preventDefault();
          e.stopPropagation();
          executeAction("speedDecrease", true, key);
          return;
        }
        if (key === "*") {
          e.preventDefault();
          e.stopPropagation();
          executeAction("speedReset", true, key);
          return;
        }
      }

      // بررسی شورتکات Shift+S برای تکرار بینهایت
      if (e.shiftKey && code === "KeyS") {
        e.preventDefault();
        e.stopPropagation();
        executeAction("repeatCurrentSubtitle", true, key);
        return;
      }

      const action = keyActionMap[code];

      if (action) {
        e.preventDefault();
        e.stopPropagation();
        executeAction(action, e.shiftKey, code);
      }
    },
    [shortcutsEnabled, executeAction],
  );

  // ============== useEffect برای کیبورد ==============
  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  // ============== useEffect برای پاک کردن interval ==============
  useEffect(() => {
    return () => {
      if (infiniteRepeatIntervalRef.current) {
        clearInterval(infiniteRepeatIntervalRef.current);
        infiniteRepeatIntervalRef.current = null;
      }
    };
  }, []);

  // ============== هوک AutoHideControls ==============
  const [showControls, setShowControls, resetActivityTimer] =
    useAutoHideControls(
      containerRef,
      videoRef,
      isPlaying,
      controlsAutoHide,
      hideControlsDelay,
      togglePlay,
      toggleFullscreen,
      toggleMute,
      handleVolumeChange,
      volume,
      handleSeek,
      isUserInteracting,
    );

  useMobileTouch(containerRef, videoRef, {
    togglePlay,
    handleSeek,
    handleVolumeChange,
    duration,
    volume,
    isMobileDevice,
    onVolumeChangeStart: () => setShowMobileVolume(true), // 👈 نمایش نشانگر
    onVolumeChangeEnd: () => setShowMobileVolume(false), // 👈 مخفی کردن نشانگر
    isAnyModalOpen,
  });

  // ============== باقی useEffectها و handlerها ==============
  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
      checkForNextRepeat(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);

      if (!hasLoadedRef.current) {
        setTimeout(() => {
          if (videoRef.current) {
            videoRef.current
              .play()
              .then(() => {
                setIsPlaying(true);
                hasLoadedRef.current = true;
              })
              .catch(console.error);
          }
        }, 100);
      }
    }
  };

  useEffect(() => {
    if (videoSrc) {
      hasLoadedRef.current = false;
      setIsPlaying(false);
    }
  }, [videoSrc, setIsPlaying]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !videoSrc) return;

    const handleCanPlay = () => {
      if (!hasLoadedRef.current) {
        video
          .play()
          .then(() => {
            setIsPlaying(true);
            hasLoadedRef.current = true;
          })
          .catch(console.error);
      }
    };

    video.addEventListener("canplay", handleCanPlay);

    return () => {
      video.removeEventListener("canplay", handleCanPlay);
    };
  }, [videoSrc, videoRef, setIsPlaying]);

  const handleMiddleClick = useCallback(
    (e) => {
      if (e.button === 1) {
        e.preventDefault();
        e.stopPropagation();
        toggleFullscreen();
        if (resetActivityTimer) resetActivityTimer();
      }
    },
    [toggleFullscreen, resetActivityTimer],
  );

  const handleVideoClick = useCallback(
    (e) => {
      if (isAnyModalOpen) return;

      if (e.button === 0) {
        togglePlay();
        if (resetActivityTimer) resetActivityTimer();
      }
    },
    [togglePlay, resetActivityTimer, isAnyModalOpen],
  );

  const handleVideoError = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    const extension = videoFileExtensionRef.current;

    if (isMacOS() && extension === "mkv") {
      setShowMacWarning(true);
      return;
    }

    const mediaError = video.error;
    let errorMessage = "فایل ویدیو خراب است یا توسط مرورگر پشتیبانی نمی‌شود.";
    if (mediaError) {
      switch (mediaError.code) {
        case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
          errorMessage = "فرمت ویدیو توسط مرورگر پشتیبانی نمی‌شود.";
          break;
      }
    }
    showInfo(errorMessage);
  }, [showInfo]);

  const handleDrop = useCallback(
    async (e) => {
      e.preventDefault();
      e.stopPropagation();

      const files = Array.from(e.dataTransfer.files);
      if (!files.length) return;

      const videoFile = files.find((f) => f.type.startsWith("video/"));
      const subtitleFiles = files.filter((f) =>
        /\.(srt|vtt|ass)$/i.test(f.name),
      );

      if (videoFile) {
        const ext = videoFile.name.split(".").pop().toLowerCase();
        videoFileExtensionRef.current = ext;
        await handleFileProcessing(videoFile);
        return;
      }

      if (subtitleFiles.length > 0) {
        setUploadedSubtitleFiles(subtitleFiles.slice(0, 2));
      }
    },
    [handleFileProcessing],
  );

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleWheel = (e) => {
      if (!videoRef.current) return;

      e.preventDefault();

      const delta = e.deltaY > 0 ? -0.05 : 0.05;
      const newVolume = Math.max(0, Math.min(1, volume + delta));

      handleVolumeChange(newVolume);

      // 👈 نمایش نشانگر صدا
      setShowDesktopVolume(true);

      // 👈 پاک کردن تایمر قبلی
      if (volumeTimeoutRef.current) {
        clearTimeout(volumeTimeoutRef.current);
      }

      // 👈 مخفی کردن نشانگر بعد از ۱.۵ ثانیه
      volumeTimeoutRef.current = setTimeout(() => {
        setShowDesktopVolume(false);
      }, 1500);

      if (resetActivityTimer) resetActivityTimer();
    };

    container.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      container.removeEventListener("wheel", handleWheel);
      if (volumeTimeoutRef.current) {
        clearTimeout(volumeTimeoutRef.current);
      }
    };
  }, [volume, handleVolumeChange, resetActivityTimer, containerRef, videoRef]);
  const handleSearchSubtitles = () => {
    setShowSubtitleNotFoundModal(false);
    setShowSearchModal(true);
  };

  const handleCloseModal = () => {
    setShowSubtitleNotFoundModal(false);
    // 🎯 پاکسازی داده‌های پیشنهاد دیگر کاربران (حالا در مودال یکپارچه ادغام شده)
    setShowOtherUsersSubtitles(false);
    setOtherUsersSubtitlesData(null);
    // ادامه پخش فیلم بعد از بستن مودال
    if (videoRef.current && videoRef.current.paused) {
      videoRef.current.play().catch(console.error);
    }
  };

  useEffect(() => {
    if (showSubtitleNotFoundModal && videoRef.current) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  }, [showSubtitleNotFoundModal]);

  const handleSearchModalClose = () => {
    setShowSearchModal(false);
  };

  useEffect(() => {
    const anyModalOpen =
      showSettings ||
      showSettingsPanel ||
      showSelectorPanel ||
      showStylePanel ||
      showShadowSettings ||
      showSearchModal ||
      showSubtitleNotFoundModal ||
      showOtherUsersSubtitles ||
      showWizardModal ||
      showCollectionSearch ||
      showSaveCollection ||
      isPackagesOpen ||
      showMobileFullscreenPrompt ||
      showDesktopFullscreenPrompt ||
      uploadedSubtitleFiles !== null ||
      isModalOpen;

    setIsAnyModalOpen(anyModalOpen);
  }, [
    showSettings,
    showSettingsPanel,
    showSelectorPanel,
    showStylePanel,
    showShadowSettings,
    showSearchModal,
    showSubtitleNotFoundModal,
    showOtherUsersSubtitles,
    showWizardModal,
    showCollectionSearch,
    showSaveCollection,
    isPackagesOpen,
    showMobileFullscreenPrompt,
    showDesktopFullscreenPrompt,
    uploadedSubtitleFiles,
    isModalOpen,
  ]);

  // محاسبه نمایش آیکون ذخیره
  const showSaveIcon = useMemo(() => {
    if (!subtitle1Loaded || !subtitle2Loaded) return false;
    const isAutoLoaded = !!(autoLoadedSub1Id && autoLoadedSub2Id);
    if (!isAutoLoaded) return true;
    const idsStillMatch =
      autoLoadedSub1Id === activeSubtitles.subtitle1?.onlineId &&
      autoLoadedSub2Id === activeSubtitles.subtitle2?.onlineId;
    return !idsStillMatch;
  }, [
    subtitle1Loaded,
    subtitle2Loaded,
    autoLoadedSub1Id,
    autoLoadedSub2Id,
    activeSubtitles,
  ]);

  // ============== JSX ==============
  return (
    <div className={`video-container ${isFullscreen ? "fullscreen" : ""}`}>
      <div
        ref={containerRef}
        className="video-wrapper"
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={handleVideoClick}
        onMouseDown={handleMiddleClick}
        tabIndex={0}
      >
        {!videoSrc && <DragDropZone onIOSWarning={onIOSWarning} />}

        {videoSrc && (
          <>
            <video
              ref={videoRef}
              src={videoSrc}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onError={handleVideoError}
              className="video-element"
              playsInline
              webkit-playsinline="true"
              autoPlay
            />
            {!isMobileDevice() && (
              <VolumeIndicator
                volume={volume}
                isVisible={showDesktopVolume}
                isMobile={false}
              />
            )}
            {/* 👈 نشانگر صدا برای موبایل (کنار چپ) */}
            {isMobileDevice() && (
              <VolumeIndicator
                volume={volume}
                isVisible={showMobileVolume}
                isMobile={true}
              />
            )}{" "}
            <SubtitleOverlay />
            {/* عنوان فیلم در بالای ویدیو */}
            {videoSrc && (
              <div
                className={`video-title-overlay ${
                  showControls && controlsAutoHide ? "visible" : "hidden"
                }`}
                onClick={(e) => e.stopPropagation()}
              >
                <span className="video-title-text">
                  {currentMovieName || "بدون عنوان"}
                </span>
                {showSaveIcon && (
                  <button
                    className="video-save-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSaveCollectionClick();
                    }}
                    title="ذخیره زیرنویس‌های فیلم"
                  >
                    <i className="fas fa-save"></i>
                  </button>
                )}
              </div>
            )}
            {showDelayBox && <DelayBox />}
            {showInfiniteIndicator && (
              <InfiniteRepeatIndicator
                isActive={showInfiniteIndicator}
                onClose={() => setShowInfiniteIndicator(false)}
              />
            )}
            {showSpeedDial && (
              <SpeedDial
                onOpenShadowSettings={() => setShowShadowSettings(true)}
              />
            )}
            {showShadowSettings && (
              <ShadowTechniqueSettings
                onClose={() => setShowShadowSettings(false)}
              />
            )}
            {showShadowProcessBox && <ShadowProcessBox />}
            {/* در بخش Controls */}
            <Controls
              showControls={showControls}
              onComparisonClick={() => setShowWizardModal(true)}
              onCollectionSearchClick={handleCollectionSearchClick}
              onSaveCollectionClick={handleSaveCollectionClick} // ✨ جدید
              onNewVideoSelect={handleNewVideoSelect} //
              onUserInteracting={setIsUserInteracting}
            />
            {/* کامپوننت جستجوی مجموعه - بعد از سایر مدال‌ها */}
            {showCollectionSearch && (
              <CollectionSubtitleSearch
                isOpen={showCollectionSearch}
                onClose={() => setShowCollectionSearch(false)}
              />
            )}
            {/* 🎯 OtherUsersSubtitlesModal حذف شد و در SubtitleNotFoundModal به‌صورت آکوردین ادغام شد */}
            {showSettingsPanel && (
              <SubtitleSettingsPanel
                onClose={() => setShowSettingsPanel(false)}
              />
            )}
            {showSelectorPanel && (
              <SubtitleSelector
                onClose={() => setShowSelectorPanel(false)}
                handleComparisonClick={() => setShowWizardModal(true)}
                handleCollectionSearchClick={handleCollectionSearchClick}
              />
            )}
            {showStylePanel && (
              <SubtitleStyle onClose={() => setShowStylePanel(false)} />
            )}
            {showSaveCollection && (
              <SaveCollectionModal
                isOpen={showSaveCollection}
                onClose={() => setShowSaveCollection(false)}
              />
            )}

            {showTutorialGuide && subtitle1Loaded && subtitle2Loaded && (
              <TutorialGuideModal
                onClose={() => setShowTutorialGuide(false)}
              />
            )}

            <MacVideoWarning
              isOpen={showMacWarning}
              onClose={() => setShowMacWarning(false)}
            />

            <NotificationContainer />
          </>
        )}

        {showMobileFullscreenPrompt && (
          <MobileFullscreenPrompt
            isOpen={showMobileFullscreenPrompt}
            onAccept={handleAcceptFullscreen}
            onDecline={handleDeclineFullscreen}
            bodyText1="برای تجربه تماشایی بهتر و لذت‌بخش‌تر، حالت تمام صفحه پیشنهاد می‌شود."
          />
        )}

        {showDesktopFullscreenPrompt && (
          <MobileFullscreenPrompt
            isOpen={showDesktopFullscreenPrompt}
            onAccept={handleAcceptDesktopFullscreen}
            onDecline={handleDeclineDesktopFullscreen}
            title="پخش تمام صفحه"
            bodyText1="برای تجربه تماشایی بهتر و لذت‌بخش‌تر، حالت تمام صفحه پیشنهاد می‌شود."
            acceptText="فعال‌سازی تمام صفحه"
            declineText="ادامه در حالت عادی"
          />
        )}

        {/* 👈👈👈 فقط وقتی MobileFullscreenPrompt بسته‌ست، مودال‌های دیگه رو نشون بده */}
        {!showMobileFullscreenPrompt && (
          <>
            {showSubtitleNotFoundModal && (
              <SubtitleNotFoundModal
                movieName={currentMovieName}
                videoMetadata={videoMetadata}
                suggestedSubtitles={otherUsersSubtitlesData}
                onSearch={handleSearchSubtitles}
                onClose={handleCloseModal}
              />
            )}

            {isLoadingSubtitles && <LoadingModal message={loadingMessage} />}

            {showSearchModal && (
              <SubtitleSearchModal onClose={handleSearchModalClose} />
            )}

            {showSettings && <SettingsPanel />}

            {showSettingsPanel && (
              <SubtitleSettingsPanel
                onClose={() => setShowSettingsPanel(false)}
              />
            )}

            {showSelectorPanel && (
              <SubtitleSelector
                onClose={() => setShowSelectorPanel(false)}
                handleComparisonClick={() => setShowWizardModal(true)}
                handleCollectionSearchClick={handleCollectionSearchClick}
              />
            )}

          </>
      )}
    </div>

      {/* این مودال‌ها بیرون از video-wrapper هستن، اونا هم شرط بذار */}
      {!showMobileFullscreenPrompt && (
        <>
          {uploadedSubtitleFiles && (
            <SubtitleUploadModal
              files={uploadedSubtitleFiles}
              onClose={() => setUploadedSubtitleFiles(null)}
              onConfirm={(config) => {
                const numbers = config.map((c) => c.subtitleNumber);
                if (new Set(numbers).size !== numbers.length) {
                  alert("نمی‌توانید دو زیرنویس را برای یک شماره انتخاب کنید");
                  return;
                }
                config.forEach((item) => {
                  loadLocalSubtitle(
                    item.file,
                    item.language,
                    item.subtitleNumber,
                  );
                });
                setUploadedSubtitleFiles(null);
              }}
            />
          )}

          {isInfiniteRepeatActive && (
            <div className="infinite-repeat-indicator">
              <i className="fas fa-infinity"></i>
              <span>تکرار بی‌نهایت فعال</span>
            </div>
          )}

          {isPackagesOpen && (
            <PackagesModal
              onClose={hidePackages}
              onSelectPackage={handleSelectPackage}
            />
          )}

          {showWizardModal && (
            <SubtitleComparisonWizard
              isOpen={showWizardModal}
              onClose={(selectedSubtitles) => {
                setShowWizardModal(false);
                if (selectedSubtitles) {
                  handleWizardComplete(selectedSubtitles);
                }
              }}
            />
          )}

          {/* 🎯 SubtitleComparisonModal حذف شد - کد مرده */}
        </>
      )}
    </div>
  );
};

export default VideoPlayer;
